/**
 * Painel de controle do blog E-FISIO: Dashboard e Artigos.
 * Configurações ficam em página própria (/admin/e-fisio/configuracoes), só para admins.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart3, BookOpen, ExternalLink, FileText, PenLine, Settings } from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { AdminShell } from "@/components/admin/shell";
import { DashboardTab } from "@/components/admin/e-fisio/DashboardTab";
import { PostsTab } from "@/components/admin/e-fisio/PostsTab";
import { getMyAccess } from "@/lib/admin/access.functions";

export const Route = createFileRoute("/admin/e-fisio")({
  head: () => ({
    meta: [
      { title: "Admin — Painel do blog E-FISIO" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/e-fisio">
      <AdminEfisioPage />
    </AdminGate>
  ),
});

const btn =
  "inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs text-white/70 hover:text-admin-accent";

function AdminEfisioPage() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [aba, setAba] = useState<"dashboard" | "artigos">("dashboard");
  const [novo, setNovo] = useState(0);
  useEffect(() => {
    getMyAccess()
      .then((a) => setIsAdmin(!!a.isMaster))
      .catch(() => setIsAdmin(false));
  }, []);

  return (
    <AdminShell
      title="Blog E-FISIO"
      description="Acompanhe o blog e escreva, edite e publique artigos."
      icon={<BookOpen className="h-6 w-6 text-admin-accent" />}
      actions={
        <>
          {isAdmin && (
            <Link to="/admin/e-fisio/configuracoes" className={btn}>
              <Settings className="h-3.5 w-3.5" /> Configurações
            </Link>
          )}
          <a href="/e-fisio" target="_blank" rel="noreferrer" className={btn}>
            <ExternalLink className="h-3.5 w-3.5" /> Ver blog
          </a>
        </>
      }
    >
      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        {(
          [
            { id: "dashboard", titulo: "DASHBOARD", desc: "Visitas, leitores e saúde do blog", Icon: BarChart3, on: () => setAba("dashboard"), ativo: aba === "dashboard" },
            { id: "artigos", titulo: "ARTIGOS", desc: "Editar, publicar e organizar os artigos", Icon: FileText, on: () => { setNovo(0); setAba("artigos"); }, ativo: aba === "artigos" },
            { id: "novo", titulo: "NOVO POST", desc: "Escrever e publicar um novo artigo", Icon: PenLine, on: () => { setAba("artigos"); setNovo((n) => n + 1); }, ativo: false },
          ] as const
        ).map(({ id, titulo, desc, Icon, on, ativo }) => (
          <button
            key={id}
            type="button"
            onClick={on}
            className={`admin-card flex flex-col items-start gap-3 p-6 text-left transition hover:border-primary ${ativo ? "border-primary ring-2 ring-primary/30" : ""}`}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
              <Icon className="h-5 w-5 text-primary" />
            </span>
            <span className="text-base font-bold tracking-tight text-foreground">{titulo}</span>
            <span className="text-sm text-muted-foreground">{desc}</span>
          </button>
        ))}
      </div>
      {aba === "dashboard" ? <DashboardTab /> : <PostsTab abrirNovo={novo} />}
    </AdminShell>
  );
}
